/**
 * 파일명에서 확장자를 견고하게 추출한다.
 *
 * 통합/문서 에이전트가 내려주는 표시명(display_name)은 LLM이 사람이 읽기 좋게
 * 가공하므로 "동적 엑셀 캘린더 템플릿 (calendar_template.xlsx)" 처럼 실제 파일명을
 * 괄호로 감싸거나 뒤에 잡다한 문자가 붙는 경우가 있다. 단순 `split('.').pop()` 은
 * 이때 "xlsx)" 를 돌려줘 모든 타입 분기가 깨진다. 아래 규칙으로 보정한다.
 *   1) 끝부분이 "( ... .ext )" 형태면 괄호 안 실제 파일명을 우선 사용
 *   2) 마지막 '.' 뒤를 취하되, 확장자 뒤에 붙은 비영숫자(닫는 괄호/공백 등)는 제거
 */
export function getFileExtension(fileName: string): string {
  if (!fileName) return '';
  let name = String(fileName).trim();
  // "사람용 제목 (real_file.xlsx)" → 괄호 안 실제 파일명 우선
  const parenMatch = name.match(/\(([^()]*\.[A-Za-z0-9]{1,10})\)\s*$/);
  if (parenMatch) name = parenMatch[1];
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  let ext = name.slice(dot + 1).toLowerCase();
  // 확장자 뒤 비영숫자(예: "xlsx)" → "xlsx") 절단
  ext = ext.replace(/[^a-z0-9].*$/, '');
  if (ext.length > 10) return '';
  return ext;
}

/** MIME 타입 → 대표 확장자 (확장자 추출 실패 시 폴백용) */
const EXCEL_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  'application/vnd.ms-excel.template.macroenabled.12',
  'text/csv',
]);
const DOCX_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const PPTX_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
]);

function normalizeMime(mimeType?: string | null): string {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

/** 파일 미리보기 가능한 확장자 목록 */
const PREVIEWABLE_EXTENSIONS = [
  'pdf', 'xlsx', 'xls', 'xlsm', 'xlsb', 'csv', 'docx', 'pptx', 'ppt',
  // 한글(HWP/HWPX)
  'hwp', 'hwpx',
  // 마크다운
  'md', 'mdx', 'markdown',
  // 이미지
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico',
  // 비디오
  'mp4', 'm4v', 'mov', 'webm', 'ogv',
  // 코드 파일
  'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
  'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'r',
  'sql', 'html', 'css', 'scss', 'sass', 'less', 'json', 'yaml', 'yml',
  'xml', 'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash', 'zsh',
  'ps1', 'bat', 'cmd', 'dockerfile', 'makefile', 'cmake',
  'lua', 'perl', 'pl', 'dart', 'vue', 'svelte', 'astro',
  'graphql', 'gql', 'proto', 'tf', 'hcl',
  'txt', 'log', 'csv', 'tsv',
];

/** 파일 미리보기 가능한 확장자 확인 (MIME 폴백 포함) */
export function canPreviewFile(fileName: string, mimeType?: string | null): boolean {
  const ext = getFileExtension(fileName);
  // 특수 파일명 처리 (Dockerfile, Makefile 등)
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';
  if (['dockerfile', 'makefile', 'cmakelists.txt'].includes(baseName)) return true;
  if (PREVIEWABLE_EXTENSIONS.includes(ext)) return true;
  // 확장자 추출이 애매한 경우 office MIME 으로 보강
  const mime = normalizeMime(mimeType);
  if (mime && (EXCEL_MIME_TYPES.has(mime) || DOCX_MIME_TYPES.has(mime) || PPTX_MIME_TYPES.has(mime))) {
    return true;
  }
  return false;
}

/** 엑셀 계열인지 확인 (확장자 우선, MIME 폴백) */
export function isExcelFile(fileName: string, mimeType?: string | null): boolean {
  if (['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(getFileExtension(fileName))) return true;
  return EXCEL_MIME_TYPES.has(normalizeMime(mimeType));
}

/** DOCX 인지 확인 (확장자 우선, MIME 폴백) */
export function isDocxFile(fileName: string, mimeType?: string | null): boolean {
  if (getFileExtension(fileName) === 'docx') return true;
  return DOCX_MIME_TYPES.has(normalizeMime(mimeType));
}

/** PPTX 인지 확인 (확장자 우선, MIME 폴백) */
export function isPptxFile(fileName: string, mimeType?: string | null): boolean {
  if (['pptx', 'ppt'].includes(getFileExtension(fileName))) return true;
  return PPTX_MIME_TYPES.has(normalizeMime(mimeType));
}

/** 한글(HWP/HWPX) 확장자인지 확인 */
export function isHwpFile(fileName: string): boolean {
  return ['hwp', 'hwpx'].includes(getFileExtension(fileName));
}

/** 마크다운 확장자인지 확인 */
export function isMarkdownFile(fileName: string): boolean {
  return ['md', 'mdx', 'markdown'].includes(getFileExtension(fileName));
}

/** 이미지 확장자인지 확인 (확장자 우선, MIME 폴백) */
export function isImageFile(fileName: string, mimeType?: string | null): boolean {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(getFileExtension(fileName))) return true;
  return normalizeMime(mimeType).startsWith('image/');
}

/** 브라우저 재생 가능한 비디오 확장자인지 확인 (확장자 우선, MIME 폴백) */
export function isVideoFile(fileName: string, mimeType?: string | null): boolean {
  if (['mp4', 'm4v', 'mov', 'webm', 'ogv'].includes(getFileExtension(fileName))) return true;
  return normalizeMime(mimeType).startsWith('video/');
}

/** 브라우저 재생 가능한 오디오 확장자인지 확인 (확장자 우선, MIME 폴백) */
export function isAudioFile(fileName: string, mimeType?: string | null): boolean {
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'webm'].includes(getFileExtension(fileName))) return true;
  return normalizeMime(mimeType).startsWith('audio/');
}

/** HTML 확장자인지 확인 (코드 보기 외에 "렌더링 미리보기" 토글 대상) */
export function isHtmlFile(fileName: string): boolean {
  return ['html', 'htm', 'xhtml'].includes(getFileExtension(fileName));
}

/** 코드 파일 확장자인지 확인 */
export function isCodeFile(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';
  // 특수 파일명
  if (['dockerfile', 'makefile', 'cmakelists.txt'].includes(baseName)) return true;
  const codeExts = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
    'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'r',
    'sql', 'html', 'css', 'scss', 'sass', 'less', 'json', 'yaml', 'yml',
    'xml', 'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash', 'zsh',
    'ps1', 'bat', 'cmd', 'lua', 'perl', 'pl', 'dart', 'vue', 'svelte', 'astro',
    'graphql', 'gql', 'proto', 'tf', 'hcl', 'txt', 'log', 'tsv',
  ];
  return codeExts.includes(ext);
}

/** 확장자 → 구문 강조 언어 매핑 */
export function getLanguageFromFileName(fileName: string): string {
  const ext = getFileExtension(fileName);
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';

  // 특수 파일명
  if (baseName === 'dockerfile') return 'docker';
  if (baseName === 'makefile' || baseName === 'cmakelists.txt') return 'makefile';

  const map: Record<string, string> = {
    js: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
    py: 'python', java: 'java', c: 'c', cpp: 'cpp', h: 'c', hpp: 'cpp',
    cs: 'csharp', go: 'go', rs: 'rust', rb: 'ruby', php: 'php',
    swift: 'swift', kt: 'kotlin', scala: 'scala', r: 'r',
    sql: 'sql', html: 'html', css: 'css', scss: 'scss', sass: 'sass', less: 'less',
    json: 'json', yaml: 'yaml', yml: 'yaml', xml: 'xml',
    toml: 'toml', ini: 'ini', cfg: 'ini', conf: 'ini', env: 'bash',
    sh: 'bash', bash: 'bash', zsh: 'bash', ps1: 'powershell', bat: 'batch', cmd: 'batch',
    lua: 'lua', perl: 'perl', pl: 'perl', dart: 'dart',
    vue: 'html', svelte: 'html', astro: 'html',
    graphql: 'graphql', gql: 'graphql', proto: 'protobuf', tf: 'hcl', hcl: 'hcl',
    txt: 'text', log: 'text', tsv: 'text',
  };
  return map[ext] || 'text';
}
